import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'

const FORMSPREE_URL = 'https://formspree.io/f/mnpngwwr'

type Status = 'idle' | 'sending' | 'sent' | 'error'

// submissions faster than this after the form appears are treated as bots
const MIN_FILL_MS = 4000

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
// international format: + country code, then 7–15 digits in total (E.164); spaces, dashes, dots and brackets allowed
const PHONE = /^\+[1-9]\d{6,14}$/

// a town or city name, optionally with country: letters in any script, spaces, hyphens, apostrophes,
// dots, commas and brackets; no digits, so e.g. "Saint-Denis", "L'Aquila", "Kyiv, Ukraine", "Östersund"
const PLACE = /^\p{L}[\p{L}\p{M}\s'’.,()-]{1,79}$/u

// start hours in 24h format: 00:00 … 23:00
const HOURS = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`)

// every date from today to 31 October (end of the Oleshky demo period), as dd/mm
function upcomingDates() {
  const dates: string[] = []
  const day = new Date()
  day.setHours(0, 0, 0, 0)
  const last = new Date(day.getFullYear(), 9, 31) // months are 0-based: 9 = October
  while (day <= last) {
    const dd = String(day.getDate()).padStart(2, '0')
    const mm = String(day.getMonth() + 1).padStart(2, '0')
    dates.push(`${dd}/${mm}`)
    day.setDate(day.getDate() + 1)
  }
  return dates
}

function isValidPlace(value: string) {
  return PLACE.test(value.trim())
}

function isValidContact(value: string) {
  const v = value.trim()
  return EMAIL.test(v) || PHONE.test(v.replace(/[\s\-.()]/g, ''))
}

// Small form under the rally schedule for people to submit their own demo. Sent via Formspree.
export function AddDemoForm() {
  const { t } = useTranslation()
  const [status, setStatus] = useState<Status>('idle')
  const [shownAt] = useState(() => Date.now())

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = e.currentTarget
    // spam: the hidden honeypot was filled in, or the form was sent too fast for a person.
    // Pretend it worked so bots get no signal, and send nothing.
    const honeypot = form.elements.namedItem('_gotcha') as HTMLInputElement
    if (honeypot.value || Date.now() - shownAt < MIN_FILL_MS) {
      setStatus('sent')
      return
    }
    const where = form.elements.namedItem('where') as HTMLInputElement
    if (!isValidPlace(where.value)) {
      where.setCustomValidity(t('rallies.addDemo.whereInvalid'))
      where.reportValidity()
      return
    }
    const contact = form.elements.namedItem('contact') as HTMLInputElement
    if (!isValidContact(contact.value)) {
      contact.setCustomValidity(t('rallies.addDemo.contactInvalid'))
      contact.reportValidity()
      return
    }
    setStatus('sending')
    try {
      const res = await fetch(FORMSPREE_URL, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' },
      })
      if (!res.ok) throw new Error(String(res.status))
      form.reset()
      setStatus('sent')
    } catch {
      setStatus('error')
    }
  }

  const inputClass =
    'w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-blue-700'

  // an empty required dropdown is :invalid, so its placeholder shows in placeholder grey
  const pickerClass = `${inputClass} invalid:text-gray-400`

  return (
    // heading in a left column beside the form on tablet and up; stacked on mobile
    <div className='mt-28 mx-auto w-full max-w-5xl flex flex-col md:flex-row md:items-start gap-2 md:gap-6 text-left text-gray-900'>
      <h3 className='text-lg font-semibold text-center md:text-left md:w-40 md:shrink-0 md:pt-1.5'>
        {t('rallies.addDemo.title')}
      </h3>
      {status === 'sent' ? (
        <p className='flex-1 text-base text-center md:text-left md:pt-1.5 opacity-80'>
          {t('rallies.addDemo.thanks')}
        </p>
      ) : (
        <form
          onSubmit={handleSubmit}
          // where / date / time / contact in one row, then message + submit
          className='flex-1 grid grid-cols-1 md:grid-cols-4 gap-2'
        >
          <input type='hidden' name='_subject' value='New demo submission - humansafari.org' />
          {/* honeypot: hidden from people, filled in by bots; Formspree also drops submissions that include it */}
          <input
            type='text'
            name='_gotcha'
            tabIndex={-1}
            autoComplete='off'
            aria-hidden='true'
            className='hidden'
          />
          <input
            name='where'
            required
            onInput={(e) => e.currentTarget.setCustomValidity('')}
            aria-label={t('rallies.addDemo.where')}
            placeholder={t('rallies.addDemo.where')}
            className={inputClass}
          />
          <select
            name='date'
            required
            defaultValue=''
            aria-label={t('rallies.addDemo.date')}
            title={t('rallies.addDemo.date')}
            className={pickerClass}
          >
            <option value='' disabled>
              {t('rallies.addDemo.datePlaceholder')}
            </option>
            {upcomingDates().map((d) => (
              <option key={d} value={d} className='text-gray-900'>
                {d}
              </option>
            ))}
          </select>
          <select
            name='time'
            required
            defaultValue=''
            aria-label={t('rallies.addDemo.time')}
            title={t('rallies.addDemo.time')}
            className={pickerClass}
          >
            <option value='' disabled>
              {t('rallies.addDemo.time')}
            </option>
            {HOURS.map((h) => (
              <option key={h} value={h} className='text-gray-900'>
                {h}
              </option>
            ))}
          </select>
          <input
            name='contact'
            required
            // clear the custom error as soon as the user edits the field
            onInput={(e) => e.currentTarget.setCustomValidity('')}
            aria-label={t('rallies.addDemo.contact')}
            placeholder={t('rallies.addDemo.contact')}
            className={inputClass}
          />
          <textarea
            name='message'
            rows={2}
            required
            // more than 50 characters
            minLength={51}
            aria-label={t('rallies.addDemo.message')}
            placeholder={t('rallies.addDemo.message')}
            className={`${inputClass} md:col-span-3`}
          />
          <button
            type='submit'
            disabled={status === 'sending'}
            className='justify-self-center self-center rounded-full bg-blue-800 text-white px-8 py-1.5 text-base hover:bg-blue-700 cursor-pointer transition disabled:opacity-60'
          >
            {status === 'sending' ? t('rallies.addDemo.sending') : t('rallies.addDemo.submit')}
          </button>
          {status === 'error' && (
            <p className='text-sm text-center text-red-700 md:col-span-4'>
              {t('rallies.addDemo.error')}
            </p>
          )}
        </form>
      )}
    </div>
  )
}
